import type { VueFormConfig } from "@/types/wizard-types";
import type {
  WizardStep as BackendWizardStep,
  WizardDefinition as BackendWizardDefinition,
} from "@/types/workflow-types";

/**
 * The builder's local step shape used inside WorkflowWizardBuilder.vue.
 * Matches the inline interface defined in that component.
 */
export interface BuilderWizardStep {
  id: string;
  title: string;
  order: number;
  type: "vueform" | "custom";
  vueformConfig?: VueFormConfig;
  customTemplate?: {
    name: string;
    componentName: string;
    description: string;
    icon: string;
  };
}

/**
 * Map a backend WizardStep (from workflow-types.ts) to the builder's local shape.
 */
export function toBuilderStep(step: BackendWizardStep): BuilderWizardStep {
  const componentType = step.componentType as "vueform" | "custom";
  const props = step.properties ?? {};

  return {
    id: step.id,
    title: step.name,
    order: step.order,
    type: componentType || "vueform",
    vueformConfig: props.vueformConfig as VueFormConfig | undefined,
    customTemplate: props.customTemplate as BuilderWizardStep["customTemplate"],
  };
}

/**
 * Map a builder step back to the backend WizardStep shape for API persistence.
 */
export function toBackendStep(step: BuilderWizardStep): BackendWizardStep {
  const properties: Record<string, any> = {};
  if (step.vueformConfig) properties.vueformConfig = step.vueformConfig;
  if (step.customTemplate) properties.customTemplate = step.customTemplate;

  return {
    id: step.id,
    name: step.title,
    componentType: step.type,
    properties,
    validationRules: [],
    isRequired: true,
    order: step.order,
  };
}

/**
 * Map a backend WizardDefinition to a list of builder steps.
 * Returns empty array if no definitions exist.
 */
export function toBuilderSteps(
  definitions: BackendWizardDefinition[] | undefined
): BuilderWizardStep[] {
  if (!definitions?.length) return [];
  const def = definitions[0];
  return def.steps.map(toBuilderStep);
}

/**
 * Build the save payload from builder steps.
 * Shape matches SaveWizardDefinitionDto on the backend.
 */
export function toSavePayload(
  workflowName: string,
  steps: BuilderWizardStep[]
) {
  return {
    name: `${workflowName} Wizard`,
    description: "",
    steps: steps.map(toBackendStep),
    isActive: true,
  };
}
